import DOMPurify from 'dompurify';
import { z } from 'zod';

/**
 * Security utilities for input validation and XSS protection
 */

// Configure DOMPurify for safe HTML rendering
const configurePurify = () => {
  DOMPurify.addHook('beforeSanitizeElements', (node: Element) => {
    // Remove potentially dangerous elements
    if (node.nodeName && ['SCRIPT', 'OBJECT', 'EMBED', 'LINK', 'STYLE'].includes(node.nodeName)) {
      node.remove();
    }
  });

  DOMPurify.addHook('beforeSanitizeAttributes', (node: Element) => {
    // Remove event handlers and dangerous attributes
    if (node.hasAttributes && node.hasAttributes()) {
      const attributes = Array.from(node.attributes);
      attributes.forEach((attr: Attr) => {
        if (attr.name.startsWith('on') || 
            ['href', 'src'].includes(attr.name) && 
            attr.value.toLowerCase().includes('javascript:')) {
          node.removeAttribute(attr.name);
        }
      });
    }
  });
};

configurePurify();

/**
 * Sanitizes HTML content to prevent XSS attacks
 */
export const sanitizeHTML = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote'],
    ALLOWED_ATTR: ['class'],
    FORBID_TAGS: ['script', 'object', 'embed', 'link', 'style', 'iframe'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
  });
};

/**
 * Sanitizes plain text input
 */
export const sanitizeText = (text: string): string => {
  return text
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim();
};

/**
 * Validates and sanitizes email input
 */
export const validateEmail = (email: string): string => {
  const sanitized = sanitizeText(email);
  const emailSchema = z.string().email().max(254);
  
  try {
    return emailSchema.parse(sanitized);
  } catch {
    throw new Error('Invalid email format');
  }
};

/**
 * Validates and sanitizes phone number
 */
export const validatePhone = (phone: string): string => {
  const sanitized = phone.replace(/[^\d+\-\s()]/g, '');
  const phoneSchema = z.string().regex(/^[\d+\-\s()]{7,20}$/);
  
  try {
    return phoneSchema.parse(sanitized);
  } catch {
    throw new Error('Invalid phone number format');
  }
};

/**
 * Validates and sanitizes name input
 */
export const validateName = (name: string): string => {
  const sanitized = sanitizeText(name);
  const nameSchema = z.string().min(1).max(100).regex(/^[\u0600-\u06FFa-zA-Z\s]+$/);
  
  try {
    return nameSchema.parse(sanitized);
  } catch {
    throw new Error('Invalid name format');
  }
};

/**
 * Validates file upload
 */
export const validateFile = (file: File, allowedTypes: string[], maxSize: number = 5 * 1024 * 1024): boolean => {
  if (!allowedTypes.includes(file.type)) {
    throw new Error('File type not allowed');
  }
  
  if (file.size > maxSize) {
    throw new Error('File too large');
  }
  
  // Check file extension matches MIME type
  const extension = file.name.split('.').pop()?.toLowerCase();
  const mimeToExt: Record<string, string[]> = {
    'image/jpeg': ['jpg', 'jpeg'],
    'image/png': ['png'],
    'image/gif': ['gif'],
    'application/pdf': ['pdf'],
    'text/plain': ['txt'],
    'application/msword': ['doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx']
  };
  
  if (extension && mimeToExt[file.type] && !mimeToExt[file.type].includes(extension)) {
    throw new Error('File extension does not match content type');
  }
  
  return true;
};

/**
 * Rate limiting utility
 */
class RateLimiter {
  private attempts: Map<string, { count: number; resetTime: number }> = new Map();
  
  isAllowed(key: string, maxAttempts: number = 5, windowMs: number = 15 * 60 * 1000): boolean {
    const now = Date.now();
    const record = this.attempts.get(key);
    
    if (!record || now > record.resetTime) {
      this.attempts.set(key, { count: 1, resetTime: now + windowMs });
      return true;
    }
    
    if (record.count >= maxAttempts) {
      return false;
    }
    
    record.count++;
    return true;
  }
  
  getRemainingTime(key: string): number {
    const record = this.attempts.get(key);
    if (!record) return 0;
    
    const remaining = record.resetTime - Date.now();
    return Math.max(0, remaining);
  }
}

export const rateLimiter = new RateLimiter();

/**
 * CSRF token generation and validation
 */
export const generateCSRFToken = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

export const storeCSRFToken = (token: string): void => {
  sessionStorage.setItem('csrf_token', token);
};

export const getCSRFToken = (): string | null => {
  return sessionStorage.getItem('csrf_token');
};

export const validateCSRFToken = (token: string): boolean => {
  const storedToken = getCSRFToken();
  return storedToken === token && token.length === 64;
};

/**
 * Common validation schemas
 */
export const validationSchemas = {
  employee: z.object({
    name: z.string().min(1).max(100),
    email: z.string().email().max(254),
    phone: z.string().regex(/^[\d+\-\s()]{7,20}$/).optional(),
    position: z.string().min(1).max(100),
    department: z.string().min(1).max(100),
  }),
  
  client: z.object({
    name_ar: z.string().min(1).max(100),
    name_en: z.string().max(100).optional(),
    mobile: z.string().regex(/^[\d+\-\s()]{7,20}$/).optional(),
    company: z.string().max(100).optional(),
  }),
  
  task: z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(1000).optional(),
    priority: z.enum(['low', 'medium', 'high', 'urgent']),
    due_date: z.string().datetime().optional(),
  }),
};

/**
 * Secure API request wrapper with CSRF protection
 */
export const secureRequest = async (
  url: string, 
  options: RequestInit = {}
): Promise<Response> => {
  const csrfToken = getCSRFToken();
  
  const secureOptions: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken || '',
      ...options.headers,
    },
  };
  
  return fetch(url, secureOptions);
};

/**
 * Input sanitization for specific contexts
 */
export const sanitizeForContext = {
  search: (input: string): string => {
    return input
      .replace(/[<>'"]/g, '')
      .replace(/[%_]/g, '\\$&') // Escape SQL wildcards
      .trim()
      .substring(0, 100);
  },
  
  filename: (input: string): string => {
    return input
      .replace(/[<>:"/\\|?*]/g, '')
      .replace(/\.\./g, '')
      .trim()
      .substring(0, 255);
  },
  
  url: (input: string): string => {
    try {
      const url = new URL(input);
      if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('Invalid protocol');
      }
      return url.toString();
    } catch {
      throw new Error('Invalid URL format');
    }
  },
};

export default {
  sanitizeHTML,
  sanitizeText,
  validateEmail,
  validatePhone,
  validateName,
  validateFile,
  rateLimiter,
  generateCSRFToken,
  storeCSRFToken,
  getCSRFToken,
  validateCSRFToken,
  validationSchemas,
  secureRequest,
  sanitizeForContext,
};